import {
  FC,
  Children,
  isValidElement,
  cloneElement,
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  useEffect,
  useCallback,
} from "react";
import { useScrollIntoView } from "src/hooks/use-scroll-into-view.hook";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import cn from "classnames";

interface Context<T> {
  highlighted: T | null;
  highlightNext: () => void;
  highlightPrev: () => void;
  onSelect: (item: T) => void;
}

const context = createContext<Context<unknown> | null>(null);

function useDataList<T>(
  items: T[],
  onSelect: (item: T) => void,
  defaultHighlighted?: T
): Context<T> {
  const [highlighted, setHighlighted] = useState<T | null>(
    defaultHighlighted || items[0] || null
  );

  const highlightNext = useCallback(() => {
    const idx = highlighted ? items.indexOf(highlighted) : -1;
    const newIdx = (idx + 1) % items.length;
    setHighlighted(items[newIdx]);
  }, [items, highlighted]);

  const highlightPrev = useCallback(() => {
    const idx = highlighted ? items.indexOf(highlighted) : 0;
    const newIdx = (idx - 1 + items.length) % items.length;
    setHighlighted(items[newIdx]);
  }, [items, highlighted]);

  useEffect(() => {
    const isValidHighlight = !!highlighted && items.includes(highlighted);
    !isValidHighlight && setHighlighted(items[0] || null);
  }, [highlighted, items]);

  return { highlighted, highlightNext, highlightPrev, onSelect };
}

export interface DataListRootProps<T = unknown> {
  items: T[];
  onSelect: (item: T) => void;
  defaultHighlighted?: T;
}

function Root<T>({
  children,
  items,
  onSelect,
  defaultHighlighted,
}: PropsWithChildren<DataListRootProps<T>>) {
  const ctxValue = useDataList(items, onSelect, defaultHighlighted);
  const { Provider } = context as React.Context<Context<T>>;
  return <Provider value={ctxValue}>{children}</Provider>;
}

const Filter: FC = ({ children }) => {
  const ctx = useContext(context);

  if (!ctx) {
    throw new Error("<DataList.Filter /> must be inside <DataList.Root />");
  }

  const Input = Children.only(children);
  const ClonedInput = !isValidElement(Input)
    ? Input
    : cloneElement(Input, {
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key === "Enter") {
            ctx.highlighted && ctx.onSelect(ctx.highlighted);
          } else if (event.key === "ArrowDown") {
            ctx.highlightNext();
          } else if (event.key === "ArrowUp") {
            ctx.highlightPrev();
          }

          typeof Input.props.onKeyDown === "function" &&
            Input.props.onKeyDown(event);
        },
      });

  return <>{ClonedInput}</>;
};

export interface DataListItemProps {
  item: unknown;
  text: string;
  highlightedClassName?: string;
}

const Item: FC<DataListItemProps> = ({
  item,
  text,
  children,
  highlightedClassName,
}) => {
  const ctx = useContext(context);
  const isHighlighted = ctx?.highlighted === item;
  const { ref } = useScrollIntoView(isHighlighted);

  if (!ctx) {
    throw new Error("<DataList.Item /> must be inside <DataList.Root />");
  }

  const Button = Children.only(children);
  const clonedButton = !isValidElement(Button)
    ? Button
    : cloneElement(Button, {
        ref, // We overwrite ref -> not nice but okay for now
        className: cn(
          Button.props.className,
          isHighlighted && highlightedClassName
        ),
      });

  return (
    <>
      {clonedButton}
      {isHighlighted && (
        <VisuallyHidden.Root aria-live="polite">
          {text} selected
        </VisuallyHidden.Root>
      )}
    </>
  );
};

export const DataList = { Root, Filter, Item };
